import { z } from 'zod';
import { CHANNEL_NAME_MAX, DISPLAY_NAME_MAX, PASSWORD_MAX } from '../constants.js';
import { channelTypeSchema } from '../domain/channel.js';
import { idSchema, isoTimestampSchema } from '../domain/primitives.js';
import { userSchema } from '../domain/user.js';
/**
 * Administrator wire contracts, for everything under `/api/v1/admin`.
 *
 * SECURITY RULE: the administrator is NOT a user. It has no row in `users`, no
 * `User` shape and no access token in the user sense - it signs in with
 * credentials from the server's environment and receives an opaque session
 * token that only the `/admin` routes accept. Nothing here is reachable with a
 * user's token, and an admin token opens no user route.
 *
 * Unlike the user directory, these shapes DO carry email addresses: an
 * administrator managing accounts needs to see who they are.
 */
// ---- Session ---------------------------------------------------------------
export const adminLoginInputSchema = z.object({
    username: z.string().trim().min(1).max(100),
    password: z.string().min(1).max(PASSWORD_MAX),
});
/**
 * What an administrator may do.
 *
 *   - `full`     - everything in the panel.
 *   - `operator` - the dashboard, the user list, and enabling or disabling
 *                  accounts. Nothing else; the server refuses every other route.
 *
 * The environment's administrator is always `full`. Directory members get the
 * role of the admin group they are in (Admin -> Directory).
 */
export const adminRoleSchema = z.enum(['full', 'operator']);
export const adminSessionSchema = z.object({
    /** Opaque bearer token, sent as `Authorization: Bearer <token>` to `/admin` routes. */
    token: z.string().min(1),
    expiresAt: isoTimestampSchema,
    username: z.string(),
    role: adminRoleSchema,
});
// ---- Listing ---------------------------------------------------------------
/**
 * Query for the admin tables.
 *
 * Offset rather than keyset pagination, unlike the transcript: these are tables
 * a person pages through and jumps around in, with a total to show, and they do
 * not have rows arriving at the head while someone reads them.
 */
export const adminListQuerySchema = z.object({
    q: z.string().trim().max(254).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).default(0),
});
export const adminChannelListQuerySchema = adminListQuerySchema.extend({
    type: channelTypeSchema.optional(),
});
// ---- Users -----------------------------------------------------------------
export const adminUserSchema = userSchema.extend({
    channelCount: z.number().int().nonnegative(),
    messageCount: z.number().int().nonnegative(),
    lastMessageAt: isoTimestampSchema.nullable(),
    /** Has at least one socket open right now. */
    online: z.boolean(),
    /** When the account was disabled; `null` while it may sign in. */
    disabledAt: isoTimestampSchema.nullable(),
    /** Who disabled it: an administrator, or the directory sync (the entry left the directory). */
    disabledBy: z.enum(['admin', 'directory']).nullable(),
    /** Too many failed sign-ins: refused until the lockout lapses or an administrator unlocks it. */
    locked: z.boolean(),
});
/**
 * Body of `PUT /admin/users/:userId/status`. A disabled account cannot sign in,
 * every session it has ends at once, and it drops out of the people picker -
 * its messages stay where they are.
 */
export const adminUserStatusInputSchema = z.object({
    disabled: z.boolean(),
});
/** Most accounts one bulk request may change; the client sends larger selections in batches. */
export const ADMIN_BULK_USERS_MAX = 500;
/** Body of `PUT /admin/users/status` - enable or disable many accounts at once. */
export const adminBulkUserStatusInputSchema = z.object({
    userIds: z.array(idSchema).min(1).max(ADMIN_BULK_USERS_MAX),
    disabled: z.boolean(),
});
/**
 * Body of `PATCH /admin/users/:userId`.
 *
 * `password` is a NEW password, set without the old one - this is the reset an
 * administrator performs for someone who is locked out. The server applies the
 * password policy and the display-name limit, exactly as for registration.
 */
export const adminUpdateUserInputSchema = z
    .object({
    displayName: z.string().trim().min(1).max(DISPLAY_NAME_MAX).optional(),
    email: z.email().max(254).optional(),
    password: z.string().min(1).max(PASSWORD_MAX).optional(),
})
    .refine((input) => input.displayName !== undefined || input.email !== undefined || input.password !== undefined, { message: 'Nothing to change' });
// ---- Channels --------------------------------------------------------------
export const adminChannelSchema = z.object({
    id: idSchema,
    type: channelTypeSchema,
    name: z.string(),
    memberIds: z.array(idSchema),
    messageCount: z.number().int().nonnegative(),
    lastMessageAt: isoTimestampSchema.nullable(),
    createdAt: isoTimestampSchema,
    /** `null` once the account that created the channel has been deleted. */
    createdBy: idSchema.nullable(),
});
export const adminChannelMemberSchema = z.object({
    id: idSchema,
    displayName: z.string(),
    email: z.string(),
    joinedAt: isoTimestampSchema,
    online: z.boolean(),
});
/** Response body of `GET /admin/channels/:channelId`. */
export const adminChannelDetailSchema = adminChannelSchema.extend({
    members: z.array(adminChannelMemberSchema),
});
/**
 * Body of `PATCH /admin/channels/:channelId`.
 *
 * Applies to GROUP channels. A direct conversation is two specific people and is
 * named after them, so renaming it or changing who is in it is refused.
 * `memberIds` is the complete new membership, not a delta.
 */
export const adminUpdateChannelInputSchema = z
    .object({
    name: z.string().trim().min(1).max(CHANNEL_NAME_MAX).optional(),
    memberIds: z.array(idSchema).min(2).max(500).optional(),
})
    .refine((input) => input.name !== undefined || input.memberIds !== undefined, {
    message: 'Nothing to change',
});
// ---- Configuration export / import ---------------------------------------------
export const CONFIG_EXPORT_FORMAT = 'orbit-sync-config';
export const CONFIG_EXPORT_VERSION = 1;
/**
 * The file `GET /admin/config/export` downloads and `POST /admin/config/import`
 * takes back: system settings and the directory configuration.
 *
 * The directory's service-account password is NOT in it - a configuration file
 * gets mailed around and checked in, and a secret must not travel with it. An
 * import keeps the password already stored. Sections are loosely typed here and
 * validated by the server against the current schemas, so a file exported by an
 * older version (missing a newer field) still imports: absent fields keep their
 * current values.
 */
export const configExportSchema = z.object({
    format: z.literal(CONFIG_EXPORT_FORMAT),
    version: z.number().int().min(1).max(CONFIG_EXPORT_VERSION),
    exportedAt: isoTimestampSchema.optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    directory: z.record(z.string(), z.unknown()).optional(),
});
//# sourceMappingURL=admin.js.map